// Axios client with token-from-localStorage and 401-redirect.
// Uses the CRA dev-server proxy (configured in package.json: "proxy") so
// requests to /api/... are forwarded to the FIBU backend on :6000 in dev.

import axios from 'axios';

const TOKEN_KEY = 'fibu.token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({ baseURL: '/api' });

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      clearToken();
      // Avoid redirect loop on the login page itself.
      if (!window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    }
    return Promise.reject(err);
  }
);

// --- API helpers ----------------------------------------------------------
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
};

export const mandantenAPI = {
  list: () => api.get('/mandanten'),
  get: (slug) => api.get(`/mandanten/${slug}`),
  patch: (slug, fields) => api.patch(`/mandanten/${slug}`, fields),
};

export const datevAPI = {
  validate: (slug, { from, to }) =>
    api.post(`/mandanten/${slug}/datev/validate`, null, { params: { from, to } }),
  fullExport: (slug, { from, to, dry_run = true, include_pdfs = false }) =>
    api.post(
      `/mandanten/${slug}/datev/full-export`,
      null,
      { params: { from, to, dry_run, include_pdfs }, responseType: 'blob' }
    ),
};

export const belegeAPI = {
  list: (slug, { status, limit = 50, offset = 0 } = {}) =>
    api.get(`/mandanten/${slug}/belege`, { params: { status, limit, offset } }),
  reviewQueue: (slug) => api.get(`/mandanten/${slug}/belege/review-queue`),
  get: (slug, id) => api.get(`/mandanten/${slug}/belege/${id}`),
  fileUrl: (slug, id) => `/api/mandanten/${slug}/belege/${id}/file`,
  upload: (slug, file, { extract = true } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    // Don't set Content-Type explicitly — axios derives the multipart boundary
    return api.post(`/mandanten/${slug}/belege/upload`, fd, { params: { extract } });
  },
  patch: (slug, id, fields) => api.patch(`/mandanten/${slug}/belege/${id}`, fields),
  buchen: (slug, id) => api.post(`/mandanten/${slug}/belege/${id}/buchen`),
  vorschlag: (slug, id) => api.get(`/mandanten/${slug}/belege/${id}/buchungsvorschlag`),
  extract: (slug, id) => api.post(`/mandanten/${slug}/belege/${id}/extract`),
  markBezahlt: (slug, id, { bezahlt_am, bezahlt_betrag }) =>
    api.patch(`/mandanten/${slug}/belege/${id}/bezahlt`, { bezahlt_am, bezahlt_betrag }),
  datevExport: (slug, from, to, dryRun = false) =>
    api.post(
      `/mandanten/${slug}/belege/datev-export`,
      null,
      { params: { from, to, dry_run: dryRun }, responseType: 'blob' }
    ),
};

export const reportsAPI = {
  susa: (slug, { from, to, include_vorgeschlagen = false } = {}) =>
    api.get(`/mandanten/${slug}/reports/susa`, {
      params: { from, to, include_vorgeschlagen },
    }),
  kontenblatt: (slug, konto, { from, to, include_vorgeschlagen = false } = {}) =>
    api.get(`/mandanten/${slug}/reports/kontenblatt`, {
      params: { konto, from, to, include_vorgeschlagen },
    }),
  ustva: (slug, { from, to, include_vorgeschlagen = false } = {}) =>
    api.get(`/mandanten/${slug}/reports/ustva`, {
      params: { from, to, include_vorgeschlagen },
    }),
  oplLieferanten: (slug, { stichtag, include_bezahlt = false } = {}) =>
    api.get(`/mandanten/${slug}/reports/opl-lieferanten`, {
      params: { stichtag, include_bezahlt },
    }),
  oplKunden: (slug, { stichtag, include_bezahlt = false } = {}) =>
    api.get(`/mandanten/${slug}/reports/opl-kunden`, {
      params: { stichtag, include_bezahlt },
    }),
};

export const buchungenAPI = {
  list: (slug, { from, to, status, konto, quelle, limit = 100, offset = 0 } = {}) =>
    api.get(`/mandanten/${slug}/buchungen`, {
      params: { from, to, status, konto, quelle, limit, offset },
    }),
  createManual: (slug, fields) => api.post(`/mandanten/${slug}/buchungen`, fields),
};

export const lieferantenAPI = {
  list: (slug, { q, limit = 100, offset = 0 } = {}) =>
    api.get(`/mandanten/${slug}/lieferanten`, { params: { q, limit, offset } }),
  get: (slug, id) => api.get(`/mandanten/${slug}/lieferanten/${id}`),
  create: (slug, fields) => api.post(`/mandanten/${slug}/lieferanten`, fields),
  patch: (slug, id, fields) => api.patch(`/mandanten/${slug}/lieferanten/${id}`, fields),
  remove: (slug, id) => api.delete(`/mandanten/${slug}/lieferanten/${id}`),
};

export const ausgangAPI = {
  list: (slug, { kunde_id, status, from, to, limit = 100, offset = 0 } = {}) =>
    api.get(`/mandanten/${slug}/ausgangsrechnungen`, {
      params: { kunde_id, status, from, to, limit, offset },
    }),
  get: (slug, id) => api.get(`/mandanten/${slug}/ausgangsrechnungen/${id}`),
  create: (slug, fields) => api.post(`/mandanten/${slug}/ausgangsrechnungen`, fields),
  patch: (slug, id, fields) => api.patch(`/mandanten/${slug}/ausgangsrechnungen/${id}`, fields),
  remove: (slug, id) => api.delete(`/mandanten/${slug}/ausgangsrechnungen/${id}`),
  buchen: (slug, id) => api.post(`/mandanten/${slug}/ausgangsrechnungen/${id}/buchen`),
  markBezahlt: (slug, id, { bezahlt_am, bezahlt_betrag }) =>
    api.patch(`/mandanten/${slug}/ausgangsrechnungen/${id}/bezahlt`, { bezahlt_am, bezahlt_betrag }),
  uploadExtract: (slug, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/mandanten/${slug}/ausgangsrechnungen/upload-extract`, fd);
  },
  fileUrl: (slug, id) => `/api/mandanten/${slug}/ausgangsrechnungen/${id}/file`,
};

export const bankAPI = {
  listKonten: (slug) => api.get(`/mandanten/${slug}/bank/konten`),
  createKonto: (slug, fields) => api.post(`/mandanten/${slug}/bank/konten`, fields),
  upload: (slug, file, bankKontoId) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/mandanten/${slug}/bank/upload`, fd, {
      params: bankKontoId ? { bank_konto_id: bankKontoId } : {},
    });
  },
  listBewegungen: (slug, { bank_konto_id, status, limit = 200, offset = 0 } = {}) =>
    api.get(`/mandanten/${slug}/bank/bewegungen`, {
      params: { bank_konto_id, status, limit, offset },
    }),
  matchVorschlaege: (slug, bewegungId) =>
    api.get(`/mandanten/${slug}/bank/bewegungen/${bewegungId}/match-vorschlaege`),
  acceptMatch: (slug, bewegungId, { beleg_id, ausgang_id }) =>
    api.post(`/mandanten/${slug}/bank/bewegungen/${bewegungId}/match`, { beleg_id, ausgang_id }),
  ignore: (slug, bewegungId) =>
    api.post(`/mandanten/${slug}/bank/bewegungen/${bewegungId}/ignore`),
};

export const kundenAPI = {
  list: (slug, { q, limit = 100, offset = 0 } = {}) =>
    api.get(`/mandanten/${slug}/kunden`, { params: { q, limit, offset } }),
  get: (slug, id) => api.get(`/mandanten/${slug}/kunden/${id}`),
  create: (slug, fields) => api.post(`/mandanten/${slug}/kunden`, fields),
  patch: (slug, id, fields) => api.patch(`/mandanten/${slug}/kunden/${id}`, fields),
  remove: (slug, id) => api.delete(`/mandanten/${slug}/kunden/${id}`),
};
